import { cn } from '@mdvip/shared/lib/utils';
import * as React from 'react';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { InfoDot } from './InfoDot';

export type FloatFieldOption = string | { value: string; label: string };

/**
 * Info hint content. A plain string renders as both the tooltip's accessible label and its
 * content (the common case). The object form is for rich content (e.g. a diagram) that needs its
 * own accessible `label` distinct from the (decorative) visual `content`, and optionally a wider
 * tooltip box than the default `max-w-48` via `contentClassName`.
 */
export type FloatFieldInfo = string | { label: string; content: React.ReactNode; contentClassName?: string };

export interface FloatFieldProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange' | 'prefix'> {
  /** Control rendered inside the box. @default 'text' */
  kind?: 'text' | 'select' | 'date';
  /** Floating label text. */
  label: string;
  /** Controlled value. */
  value?: string;
  /** Change handler — receives an event-like `{ target: { value } }` for both input and select. */
  onChange?: (event: { target: { value: string } }) => void;
  /** Options for `kind="select"` — strings or `{ value, label }`. */
  options?: FloatFieldOption[];
  /** Static prefix shown before the value once floated (e.g. "$"). */
  prefix?: string;
  /** Info hint — renders an `InfoDot` tooltip (text fields only). */
  info?: FloatFieldInfo;
  /** Error state — delegates to the control's `aria-invalid` styling + destructive label. @default false */
  error?: boolean;
  /** Helper / error text under the box. */
  hint?: React.ReactNode;
  /** @default false */
  disabled?: boolean;
  /** Extra props spread onto the underlying Input (`type`, `inputMode`, `maxLength`, …). */
  inputProps?: React.InputHTMLAttributes<HTMLInputElement> & { className?: string };
  /** md = 48px, lg = 56px. @default 'md' */
  size?: 'md' | 'lg';
  /** Associates the floating `<label>` with the control. Always required for a11y. */
  id: string;
}

/**
 * FloatField — boxed field with a floating label (text / select / date).
 *
 * A thin composition layer over the shared `Input` / `Select`: it adds a relative wrapper + a
 * floating `<label>` and standardizes the height to 48px (md) / 56px (lg). It does NOT
 * re-implement the field — the control is the real primitive, so its focus ring, invalid state
 * and tokens are inherited. Float state is derived from focus + value; error styling is
 * delegated to `aria-invalid`. Self-labeling — don't wrap it in a `FieldLabel`.
 *
 * Forwards its ref to the underlying `Input` (`kind="text"`/`"date"` only — `kind="select"` never
 * forwarded one either, matching the old `FormSelectField`'s behavior) so `FormFloatField` can
 * register the DOM node with react-hook-form for `shouldFocusError`.
 */
export const FloatField = React.forwardRef<HTMLInputElement, FloatFieldProps>(function FloatField(
  {
    kind = 'text',
    label,
    value = '',
    onChange,
    options = [],
    prefix,
    info,
    error = false,
    hint,
    disabled = false,
    inputProps = {},
    size = 'md',
    id,
    className,
    ...rest
  },
  ref,
) {
  const [focused, setFocused] = React.useState(false);
  const h = size === 'lg' ? 'h-14' : 'h-12';
  const padding = size === 'lg' ? 'pt-[1.4rem] pb-[0.15rem]' : 'pt-[1.15rem] pb-[0.15rem]';
  const hasValue = value !== undefined && value !== null && value.length > 0;
  // kind="date" always shows the native date control, unless a caller explicitly overrides it.
  const type = inputProps.type ?? (kind === 'date' ? 'date' : 'text');
  const floated = focused || hasValue || kind === 'date';

  const labelClassName = cn(
    'pointer-events-none absolute left-3 z-10 whitespace-nowrap transition-all duration-150',
    error ? 'text-destructive' : focused ? 'text-foreground' : 'text-muted-foreground',
    floated ? cn('translate-y-0 text-xs', size === 'lg' ? 'top-2' : 'top-1.5') : 'top-1/2 -translate-y-1/2 text-sm',
  );

  if (kind === 'select') {
    return (
      <div data-slot="float-field" className={cn('min-w-0', className)} {...rest}>
        <div className="relative">
          <Select
            value={value}
            disabled={disabled}
            onValueChange={(val: string | null) => onChange?.({ target: { value: val ?? '' } })}
          >
            <SelectTrigger
              id={id}
              size="none"
              aria-invalid={error || undefined}
              className={cn('w-full', h)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
            >
              <SelectValue
                placeholder=""
                className={cn(
                  'text-sm text-foreground',
                  floated && (size === 'lg' ? 'translate-y-2' : 'translate-y-[7px]'),
                )}
              />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => {
                const optionValue = typeof option === 'string' ? option : option.value;
                const optionLabel = typeof option === 'string' ? option : option.label;
                return (
                  <SelectItem key={optionValue} value={optionValue}>
                    {optionLabel}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <label htmlFor={id} className={labelClassName}>
            {label}
          </label>
        </div>
        {hint && <p className={cn('mt-1.5 text-xs', error ? 'text-destructive' : 'text-muted-foreground')}>{hint}</p>}
      </div>
    );
  }

  const { className: inputClassName, ...restInputProps } = inputProps;

  return (
    <div data-slot="float-field" className={cn('min-w-0', className)} {...rest}>
      <div className="relative">
        <Input
          ref={ref}
          id={id}
          type={type}
          value={value}
          disabled={disabled}
          placeholder=" "
          aria-invalid={error || undefined}
          onChange={onChange as React.ChangeEventHandler<HTMLInputElement>}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className={cn('w-full', h, padding, prefix && 'pl-6', info && 'pr-9', inputClassName)}
          {...restInputProps}
        />
        {prefix && (
          <span
            className={cn(
              // Overlays the input's own content box (same height, same asymmetric padding that
              // reserves room for the floated label) and centres the glyph in it, so the prefix
              // shares the value's baseline instead of relying on a hand-tuned offset. Inherits the
              // Input's responsive type scale for the same reason.
              'pointer-events-none absolute inset-y-0 left-3 flex items-center text-base md:text-sm',
              padding,
              floated ? 'text-foreground' : 'text-transparent',
            )}
          >
            {prefix}
          </span>
        )}
        {info &&
          (typeof info === 'string' ? (
            <span className="absolute right-3 top-1/2 -translate-y-1/2">
              <InfoDot text={info} />
            </span>
          ) : (
            <span className="absolute right-3 top-1/2 -translate-y-1/2">
              <InfoDot text={info.label} contentClassName={info.contentClassName}>
                {info.content}
              </InfoDot>
            </span>
          ))}
        <label htmlFor={id} className={labelClassName}>
          {label}
        </label>
      </div>
      {hint && <p className={cn('mt-1.5 text-xs', error ? 'text-destructive' : 'text-muted-foreground')}>{hint}</p>}
    </div>
  );
});
